import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  AddRuleInput,
  CreateWorkflowInput,
  DataEnvelope,
  MoveRuleInput,
  UpdateRuleInput,
  UpdateWorkflowInput,
  Workflow,
} from './types';

export const workflowsKey = ['workflows'] as const;

export function useWorkflows() {
  return useQuery({
    queryKey: workflowsKey,
    queryFn: ({ signal }) => api<DataEnvelope<Workflow[]>>('/workflows', { signal }).then((r) => r.data),
  });
}

export function useSaveWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CreateWorkflowInput | UpdateWorkflowInput }) =>
      (id
        ? api<DataEnvelope<Workflow>>(`/workflows/${id}`, { method: 'PATCH', body: input })
        : api<DataEnvelope<Workflow>>('/workflows', { method: 'POST', body: input })
      ).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workflowsKey }),
  });
}

export function useDeleteWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/workflows/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workflowsKey }),
  });
}

export function useAddWorkflowRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workflowId, input }: { workflowId: string; input: AddRuleInput }) =>
      api<DataEnvelope<Workflow>>(`/workflows/${workflowId}/rules`, { method: 'POST', body: input }).then(
        (r) => r.data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workflowsKey }),
  });
}

export function useUpdateWorkflowRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workflowId,
      ruleId,
      input,
    }: {
      workflowId: string;
      ruleId: string;
      input: UpdateRuleInput;
    }) =>
      api<DataEnvelope<Workflow>>(`/workflows/${workflowId}/rules/${ruleId}`, {
        method: 'PATCH',
        body: input,
      }).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workflowsKey }),
  });
}

export function useRemoveWorkflowRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workflowId, ruleId }: { workflowId: string; ruleId: string }) =>
      api<DataEnvelope<Workflow>>(`/workflows/${workflowId}/rules/${ruleId}`, { method: 'DELETE' }).then(
        (r) => r.data,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workflowsKey }),
  });
}

export function useMoveWorkflowRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workflowId,
      ruleId,
      input,
    }: {
      workflowId: string;
      ruleId: string;
      input: MoveRuleInput;
    }) =>
      api<DataEnvelope<Workflow>>(`/workflows/${workflowId}/rules/${ruleId}/move`, {
        method: 'POST',
        body: input,
      }).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workflowsKey }),
  });
}
